import React, { createContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const {
  addEventListener,
  cancelAnimationFrame,
  removeEventListener,
  requestAnimationFrame,
} = window;

export const cssVariableConsumerDefault = ({ height, width }) => {
  // this is used instead of useContext so that the iframe doesn't get rerendered any time a height changes
  // this logic keeps the iframe the correct size and allows the video service provider to handle the responsive behavior
  let vh = height * 0.01;
  let vw = width * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  document.documentElement.style.setProperty('--vw', `${vw}px`);

  return null;
};

// this object implements the default version of using 100% vh 100% vw which resolves the viewable height and width issues in mobile broswers
export const cssVariableDefaultStyles = {
  height: 'calc(var(--vh, 1vh) * 100)',
  width: 'calc(var(--vw, 1vw) * 100)',
};

const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  let animationFrameRef = useRef();

  useEffect(() => {
    const handleResize = () => {
      if (!!animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        setDimensions({
          height: window.innerHeight,
          width: window.innerWidth,
        });
      });
    };
    addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      removeEventListener('resize', handleResize);
    };
  }, []);

  return dimensions;
};

export const WindowDimensionsCtx = createContext(null);

const WindowDimensionsProvider = ({ children }) => {
  const dimension = useWindowDimensions();
  return (
    <WindowDimensionsCtx.Provider value={dimension}>
      {children}
    </WindowDimensionsCtx.Provider>
  );
};

WindowDimensionsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export default WindowDimensionsProvider;
/**
 * handle the consumer of the windowDimensionsProvider to set a global css variable, the css variable can be used on a element to resize based on window demensions
 * @param {object} demensions
 * @param {number} demensions.height
 * @param {number} demensions.width
 */
export const WindowDimensionsConsumer = ({ Context, children }) => {
  return <Context.Consumer>{children}</Context.Consumer>;
};

WindowDimensionsConsumer.defaultProps = {
  Context: WindowDimensionsCtx,
  children: cssVariableConsumerDefault,
};

WindowDimensionsConsumer.propTypes = {
  Context: PropTypes.object,
  children: PropTypes.func,
};
